import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import DSBox from "views/system-design/DSBox";
import DSButton from "views/system-design/DSButton";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSFormControl from "views/system-design/DSFormControl";
import DSInputLabel from "views/system-design/DSInputLabel";
import DSMenuItem from "views/system-design/DSMenuItem";
import DSSectionHeading from "views/system-design/DSSectionHeading";
import DSSelect from "views/system-design/DSSelect";
import DSTypography from "views/system-design/DSTypography";
import { sectionCardSx } from "views/system-design/sectionCardSx";
import AudioPlayer from "components/AudioPlayer/AudioPlayer";
import collectionApiClient from "../../../api/collection";
import { Klydo } from "../../../api/klydo";
import { MachineCollection } from "../../../Types";
import { getChimeDisplayName } from "../../../utils/chimeUtils";

const FIELD = "defaultCustomChimeId";

interface ProfileDefaultChimeCardProps {
  values: Record<string, unknown>;
  collections: MachineCollection[];
  onChange: (field: string, value: unknown) => void;
}

/**
 * Same picker as DeviceDefaultChimeCard, but the choices come from the
 * profile's own collections: a chime only plays if its klydo is on the
 * clock, and Sync puts exactly these collections there. Like every profile
 * setting, the key lands in `values` only once picked; "" pushes the
 * firmware default chime.
 */
const ProfileDefaultChimeCard = ({
  values,
  collections,
  onChange,
}: ProfileDefaultChimeCardProps) => {
  const collectionQueries = useQueries({
    queries: collections.map(({ id }) => ({
      queryKey: ["collection", id],
      queryFn: () => collectionApiClient.getCollectionById(id),
    })),
  });

  const klydosWithChimes = useMemo(() => {
    const seen = new Set<string>();
    const result: Klydo[] = [];
    for (const query of collectionQueries) {
      for (const klydo of query.data?.klydos || []) {
        if (!seen.has(klydo.id) && klydo.chimesUrls?.length) {
          seen.add(klydo.id);
          result.push(klydo);
        }
      }
    }
    return result;
  }, [collectionQueries]);

  const currentId = (values[FIELD] as string | undefined) ?? "";
  const selectedKlydo = klydosWithChimes.find((k) => k.id === currentId);
  const isLoading = collectionQueries.some((query) => query.isLoading);
  // Collections changed after the chime was picked: Sync would push a chime
  // the clock can't play.
  const orphaned = !!currentId && !isLoading && !selectedKlydo;

  return (
    <DSCard sx={[sectionCardSx, { mb: 2 }]}>
      <DSSectionHeading>Default Chime</DSSectionHeading>
      <DSCardContent>
        {isLoading ? (
          <DSTypography variant="body2">Loading chimes...</DSTypography>
        ) : klydosWithChimes.length === 0 ? (
          <DSTypography variant="body2" sx={{ color: "text.secondary" }}>
            No klydos with chimes in this profile&apos;s collections
          </DSTypography>
        ) : (
          <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <DSFormControl fullWidth size="small">
              <DSInputLabel id="profile-default-chime-label">
                Chime klydo
              </DSInputLabel>
              <DSSelect
                labelId="profile-default-chime-label"
                label="Chime klydo"
                value={selectedKlydo ? currentId : ""}
                onChange={(e) => onChange(FIELD, e.target.value as string)}
              >
                <DSMenuItem value="">Firmware default</DSMenuItem>
                {klydosWithChimes.map((klydo) => (
                  <DSMenuItem key={klydo.id} value={klydo.id}>
                    {klydo.name || klydo.id}
                  </DSMenuItem>
                ))}
              </DSSelect>
            </DSFormControl>
            {selectedKlydo?.chimesUrls?.[0] && (
              <AudioPlayer
                src={selectedKlydo.chimesUrls[0]}
                title={getChimeDisplayName(
                  selectedKlydo.chimesUrls[0],
                  0,
                  selectedKlydo.metadata?.chimesFileName,
                )}
              />
            )}
          </DSBox>
        )}
        {orphaned && (
          <DSTypography variant="caption" color="error">
            The chosen chime ({currentId}) is not in this profile&apos;s
            collections anymore. Pick another one.
          </DSTypography>
        )}
        {/* The select is hidden without chime klydos, so an orphaned pick
            would otherwise be stuck. */}
        {!!currentId && (
          <DSBox sx={{ mt: 1 }}>
            <DSButton size="small" onClick={() => onChange(FIELD, "")}>
              Reset to firmware default
            </DSButton>
          </DSBox>
        )}
      </DSCardContent>
    </DSCard>
  );
};

export default ProfileDefaultChimeCard;
